import React, { useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import {
  Authenticator,
  CheckboxField,
  TextField,
  View,
  Flex,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box } from "theme-ui";
import {
  Heading,
  Button as LumosButton,
  AlertInline,
  useNotify,
} from "@loanpal/lumos__core";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import Loading from "../../utils/loading";
import {
  ADMIN_ERROR_EMAIL_TEMPLATES,
} from "../../utils/admin-error-email-templates";
import {
  getAdminErrorEmailConfig,
  saveAdminErrorEmailConfig,
} from "../../utils/api";
import awsExports from "../../aws-exports";

Amplify.configure(awsExports);

const CONFIGURABLE_TEMPLATES = ADMIN_ERROR_EMAIL_TEMPLATES.filter(
  (t) => t.id !== "T00-OTHER"
);

const AdminErrorEmailSettings = () => {
  const { addToast } = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [enabledIds, setEnabledIds] = useState(() => new Set());
  const [templateRoutes, setTemplateRoutes] = useState({});

  const applyConfig = (config) => {
    setEnabledIds(new Set(config?.autoEmailTemplateIds || []));
    setTestRecipient(config?.testRecipient || "");
    setTemplateRoutes(config?.templateTestRecipients || {});
    setSource(config?.source || "");
    setUpdatedAt(config?.updatedAt || null);
  };

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const config = await getAdminErrorEmailConfig();
      applyConfig(config);
    } catch (err) {
      console.error(err);
      setError(
        "Could not load email alert settings. Deploy the API/function routes if this is the first time."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const toggleTemplate = (id, checked) => {
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setTemplateRoute = (id, email) => {
    setTemplateRoutes((prev) => ({
      ...prev,
      [id]: email,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const cleanedRoutes = {};
      for (const [id, email] of Object.entries(templateRoutes)) {
        const trimmed = String(email ?? "").trim();
        if (trimmed) cleanedRoutes[id] = trimmed;
      }
      const saved = await saveAdminErrorEmailConfig({
        autoEmailTemplateIds: [...enabledIds],
        testRecipient,
        templateTestRecipients: cleanedRoutes,
      });
      applyConfig(saved);
      addToast({
        variant: "success",
        message: "Email alert settings saved",
      });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to save settings");
      addToast({
        variant: "error",
        message: "Failed to save email alert settings",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Authenticator formFields={formFields} components={components}>
      {({ signOut }) => (
        <>
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>
          <Box sx={{ p: 4, maxWidth: 920, mx: "auto" }}>
            <Heading as="h1" variant="h3" sx={{ mb: 2 }}>
              Error email alerts
            </Heading>
            <Box sx={{ color: "text.secondary", mb: 3, fontSize: 1 }}>
              Enable templates to auto-send, and optionally set route-to email(s)
              for testing (To only, no CC). Use commas for multiple addresses.
              Per-template route wins over the global test recipient. Leave both
              blank for production partner / admin routing.
            </Box>

            {loading ? (
              <Loading />
            ) : (
              <View>
                {error ? (
                  <Box sx={{ mb: 3 }}>
                    <AlertInline variant="error">{error}</AlertInline>
                  </Box>
                ) : null}

                <Box
                  sx={{
                    mb: 3,
                    fontSize: 0,
                    color: "text.secondary",
                  }}
                >
                  Source: {source || "unknown"}
                  {updatedAt ? ` · Last saved ${updatedAt}` : ""}
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Heading as="h2" variant="h5" sx={{ mb: 2 }}>
                    Global test recipient (fallback)
                  </Heading>
                  <TextField
                    label="Global test email (optional)"
                    labelHidden
                    placeholder="you+errors@goodleap.com, teammate@goodleap.com"
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    descriptiveText="Comma-separated. Used only when a template has no route-to email of its own."
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Heading as="h2" variant="h5" sx={{ mb: 2 }}>
                    Auto-send templates
                  </Heading>
                  <Flex direction="column" gap="1.25rem">
                    {CONFIGURABLE_TEMPLATES.map((template) => (
                      <Box
                        key={template.id}
                        sx={{
                          borderTop: "1px solid",
                          borderColor: "muted",
                          pt: 3,
                        }}
                      >
                        <CheckboxField
                          name={template.id}
                          label={`${template.id} — ${template.label || template.issueDetected}`}
                          checked={enabledIds.has(template.id)}
                          onChange={(e) =>
                            toggleTemplate(template.id, e.target.checked)
                          }
                        />
                        <Box sx={{ mt: 2, pl: 4 }}>
                          <TextField
                            label={`Route ${template.id} to`}
                            labelHidden
                            placeholder="a@goodleap.com, b@goodleap.com"
                            value={templateRoutes[template.id] || ""}
                            onChange={(e) =>
                              setTemplateRoute(template.id, e.target.value)
                            }
                            descriptiveText="Comma-separated test override for this template only."
                          />
                        </Box>
                      </Box>
                    ))}
                  </Flex>
                </Box>

                <Flex gap="1rem">
                  <LumosButton
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save settings"}
                  </LumosButton>
                  <LumosButton
                    variant="secondary"
                    onClick={loadConfig}
                    disabled={saving || loading}
                  >
                    Reload
                  </LumosButton>
                </Flex>
              </View>
            )}
          </Box>
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  signOut: { marginRight: 10 },
};

export default AdminErrorEmailSettings;
